import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../common/Toast';
import { useI18n } from '../../i18n/I18nContext';
import './AuthPages.css';

function LoginPage() {
  const { t } = useI18n();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [lockout, setLockout]   = useState(null);
  const [warning, setWarning]   = useState(null);
  const [countdown, setCountdown] = useState(null);

  const { login } = useAuth();
  const navigate  = useNavigate();
  const addToast  = useToast();

  const startCountdown = (minutes) => {
    let secs = minutes * 60;
    setCountdown(secs);
    const interval = setInterval(() => {
      secs -= 1;
      if (secs <= 0) {
        clearInterval(interval);
        setCountdown(null);
        setLockout(null);
        setError('');
      } else {
        setCountdown(secs);
      }
    }, 1000);
  };

  const formatCountdown = (secs) => {
    if (!secs) return '';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLockout(null);
    setWarning(null);
    setLoading(true);

    try {
      await login(email, password);
      addToast(t.auth.connSuccess);
      navigate('/');
    } catch (err) {
      if (err.verrouille) {
        setLockout({ minutes_restantes: err.minutes_restantes || 30 });
        startCountdown((err.minutes_restantes || 30) * 60);
      } else if (err.avertissement) {
        setError(err.message);
        setWarning({ tentatives_restantes: err.tentatives_restantes, message: err.avertissement });
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const isLocked = !!lockout;

  return (
    <div className="auth-page">
      <div className="auth-card glass-card">
        <h2>{t.auth.loginTitle}</h2>
        <p className="auth-subtitle">{t.auth.loginSubtitle}</p>

        {/* Verrouillage */}
        {isLocked && (
          <div className="auth-lockout-banner" role="alert">
            <span className="auth-lockout-icon">🔒</span>
            <div>
              <strong>{t.auth.locked}</strong>
              <p>
                {t.auth.lockedMsg}
                {countdown
                  ? <> <span className="auth-lockout-timer">{formatCountdown(countdown)}</span></>
                  : ` ${lockout.minutes_restantes} min`}.
              </p>
            </div>
          </div>
        )}

        {/* Erreur */}
        {!isLocked && error && <div className="auth-error">{error}</div>}

        {/* Avertissement tentatives restantes */}
        {!isLocked && warning && (
          <div className="auth-warning" role="alert">
            <span>⚠️</span>
            <span>{warning.message}</span>
            <div className="auth-warning-dots">
              {Array.from({ length: 3 }).map((_, i) => (
                <span key={i} className={`auth-warning-dot ${i < warning.tentatives_restantes ? 'auth-warning-dot--live' : 'auth-warning-dot--gone'}`} />
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>{t.auth.email}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="votre@email.com"
              disabled={isLocked || loading}
              required
            />
          </div>

          <div className="form-group">
            <label>{t.auth.password}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={isLocked || loading}
              required
            />
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading || isLocked}>
            {loading ? t.auth.loginLoading : isLocked ? t.auth.lockedBtn : t.auth.loginBtn}
          </button>
        </form>

        <p className="auth-footer">
          {t.auth.noAccount} <Link to="/register">{t.auth.createAccount}</Link>
        </p>
      </div>
    </div>
  );
}

export default LoginPage;
